import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Camera, Loader2, Lock, Sparkles, CheckCircle2, AlertTriangle, HelpCircle } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../lib/i18n";
import { useSeo } from "../../lib/useSeo";
import { PageHeader } from "../../components/PageHeader";
import { PremiumPill } from "../../components/PremiumPill";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const FotoAnalyse = () => {
  const { t } = useT();
  useSeo({ title: t("tools.photo.seoTitle"), description: t("tools.photo.seoDesc"), path: "/foto-analyse" });
  const { user } = useAuth();
  const [search, setSearch] = useSearchParams();
  const [listings, setListings] = useState(null);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(search.get("listing") || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const isPremium = !!user?.is_premium;

  useEffect(() => {
    if (!isPremium) return;
    api("/listings?sort=match_desc")
      .then((r) => setListings(r.listings || []))
      .catch(() => setListings([]));
  }, [isPremium]);

  const filtered = useMemo(() => {
    const items = listings || [];
    if (!q.trim()) return items.slice(0, 30);
    const needle = q.toLowerCase();
    return items
      .filter((l) =>
        (l.title || "").toLowerCase().includes(needle) ||
        (l.city || "").toLowerCase().includes(needle) ||
        (l.street || "").toLowerCase().includes(needle)
      )
      .slice(0, 30);
  }, [listings, q]);

  const selected = listings?.find((l) => l.id === selectedId);

  const analyse = async () => {
    if (!selectedId) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await api("/photo-analysis", {
        method: "POST",
        body: JSON.stringify({ listing_id: selectedId }),
      });
      setResult(res);
    } catch (e) {
      setError(e.message || t("tools.photo.error"));
    } finally {
      setBusy(false);
    }
  };

  const scoreColour = (s) => {
    if (typeof s !== "number") return "bg-slate-200 text-slate-600";
    if (s >= 75) return "bg-success/15 text-success";
    if (s >= 50) return "bg-warning/20 text-warning";
    return "bg-danger/15 text-danger";
  };

  if (!isPremium) {
    return (
      <div>
        <PageHeader icon={Camera} title={t("tools.photo.title")} subtitle={t("tools.photo.subtitle")} />
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
          <div className="rounded-3xl border border-amber-300/60 bg-gradient-to-br from-amber-50/40 via-white to-white p-10 text-center shadow-sm">
            <Lock className="mx-auto text-amber-500" size={36} />
            <h2 className="mt-4 font-display text-2xl font-extrabold text-navy">{t("tools.locked.title")}</h2>
            <p className="mx-auto mt-2 max-w-lg text-slate-600">{t("tools.locked.sub")}</p>
            <Button
              asChild
              size="lg"
              className="mt-6 rounded-full bg-navy text-amber-300 hover:bg-navy/90"
              data-testid="photo-unlock-cta"
            >
              <Link to="/prijzen">
                <Sparkles size={16} className="mr-2" /> {t("tools.locked.cta")}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="photo-page">
      <PageHeader
        icon={Camera}
        title={t("tools.photo.title")}
        subtitle={t("tools.photo.subtitle")}
        right={<PremiumPill active labelActive={t("premium.pill.active")} />}
      />

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-2">
        {/* LEFT: picker */}
        <div className="rounded-3xl border border-amber-300/50 bg-gradient-to-br from-amber-50/30 via-white to-white p-6 shadow-sm">
          <label className="font-display text-sm font-bold text-navy">{t("tools.photo.pickListing")}</label>
          <Input
            data-testid="photo-search-input"
            className="mt-2 rounded-xl"
            placeholder={t("tools.photo.searchPh")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="mt-3 max-h-[340px] overflow-y-auto rounded-2xl border border-border">
            {listings === null ? (
              <div className="p-6 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">{t("tools.coach.noneFound")}</div>
            ) : (
              filtered.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`photo-listing-${l.id}`}
                  onClick={() => { setSelectedId(l.id); setSearch({ listing: l.id }); }}
                  className={`flex w-full items-center gap-3 border-b border-border p-3 text-left last:border-b-0 hover:bg-slate-50 ${selectedId === l.id ? "bg-amber-50/60" : ""}`}
                >
                  <div className="h-12 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                    {l.image && <img src={l.image} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-navy">{l.title}</div>
                    <div className="truncate text-xs text-slate-500">{l.city} · €{l.price}/mnd</div>
                  </div>
                </button>
              ))
            )}
          </div>
          <Button
            data-testid="photo-analyse-btn"
            disabled={!selectedId || busy}
            onClick={analyse}
            className="mt-5 w-full rounded-full bg-navy text-amber-300 hover:bg-navy/90"
            size="lg"
          >
            {busy ? (
              <><Loader2 className="mr-2 animate-spin" size={16} /> {t("tools.photo.analysing")}</>
            ) : (
              <><Sparkles size={16} className="mr-2" /> {t("tools.photo.analyse")}</>
            )}
          </Button>
          {error && <p data-testid="photo-error" className="mt-3 text-sm text-danger">{error}</p>}
        </div>

        {/* RIGHT: result */}
        <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          {!result ? (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center text-slate-400">
              <Camera size={40} />
              <p className="mt-4 text-sm">
                {selected ? t("tools.photo.readyHint", { title: selected.title }) : t("tools.photo.emptyHint")}
              </p>
            </div>
          ) : (
            <div data-testid="photo-result">
              <div className="flex items-center gap-4">
                <div className={`flex h-16 w-16 items-center justify-center rounded-2xl font-display text-2xl font-extrabold ${scoreColour(result.overall_score)}`}>
                  {typeof result.overall_score === "number" ? `${result.overall_score}` : "?"}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-amber-600">{t("tools.photo.result.score")}</div>
                  <p className="mt-1 text-sm text-slate-700">{result.summary}</p>
                </div>
              </div>

              {result.positives?.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">{t("tools.photo.result.positives")}</div>
                  <ul className="mt-2 space-y-2">
                    {result.positives.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-success" /> <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.concerns?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">{t("tools.photo.result.concerns")}</div>
                  <ul className="mt-2 space-y-2">
                    {result.concerns.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" /> <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.questions_to_ask?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">{t("tools.photo.result.questions")}</div>
                  <ul className="mt-2 space-y-2">
                    {result.questions_to_ask.map((qq, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <HelpCircle size={16} className="mt-0.5 shrink-0 text-brandblue" /> <span>{qq}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {typeof result.photo_count === "number" && (
                <p className="mt-6 text-xs text-slate-400">{t("tools.photo.result.photoCount", { n: result.photo_count })}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FotoAnalyse;
